<template>
  <div class="key-issues-section">
    <h4>Key Issues</h4>
    <div class="key-issues-tabs">
      <Button type="button" @click="$emit('update:activeTab', 'revenue')" :class="['key-issues-btn', { active: activeTab === 'revenue' }]">Revenue Impact</Button>
      <Button type="button" @click="$emit('update:activeTab', 'budgetary')" :class="['key-issues-btn', { active: activeTab === 'budgetary' }]">Budgetary Impact</Button>
      <Button type="button" @click="$emit('update:activeTab', 'dof')" :class="['key-issues-btn', { active: activeTab === 'dof' }]">Other DOF Concerns</Button>
    </div>
    <div class="key-issues-content">
      <div v-show="activeTab === 'revenue'" class="key-issues-panel">
        <div class="year-range-controls">
          <div class="form-group">
            <label>Start Year:</label>
            <DatePicker v-model="localRevenueStartYearDate" view="year" dateFormat="yy" @date-select="updateRevenueStartYear" />
          </div>
          <div class="form-group">
            <label>End Year:</label>
            <DatePicker v-model="localRevenueEndYearDate" view="year" dateFormat="yy" @date-select="updateRevenueEndYear" />
          </div>
        </div>
        <div class="table-container pb-2">
          <table class="revenue-table">
            <thead>
              <tr>
                <th class="particulars-col">Particulars</th>
                <th v-for="year in revenueYearRange" :key="year">{{ year }}</th>
                <th class="delete-col">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in (keyIssues && keyIssues.revenue_rows) || []" :key="idx">
                <td class="particulars-col">
                  <Textarea v-model="row.particulars" placeholder="Enter particulars" rows="2" @input="$emit('autoResize', $event)"></Textarea>
                </td>
                <td v-for="year in revenueYearRange" :key="year">
                  <InputNumber v-model="row[year]" :min-fraction-digits="2" :max-fraction-digits="2" placeholder="0.00" :class="['revenue-input', { 'negative-value': row[year] < 0 }]" :use-grouping="true" mode="currency" currency="PHP" locale="en-PH" />
                </td>
                <td class="delete-col">
                  <button type="button" @click="$emit('deleteRevenueRow', idx)" class="delete-row-btn">Delete</button>
                </td>
              </tr>
              <tr class="total-row">
                <td class="particulars-col total-label">TOTAL</td>
                <td v-for="year in revenueYearRange" :key="year" class="total-cell">
                  {{ getYearTotal(keyIssues?.revenue_rows, year) }}
                </td>
                <td class="delete-col"></td>
              </tr>
            </tbody>
          </table>
        </div>
        <button type="button" @click="$emit('addRevenueRow')" class="add-row-btn">Add Row</button>
      </div>
      <div v-show="activeTab === 'budgetary'" class="key-issues-panel">
        <div class="year-range-controls">
          <div class="form-group">
            <label>Start Year:</label>
            <DatePicker v-model="localBudgetaryStartYearDate" view="year" dateFormat="yy" @date-select="updateBudgetaryStartYear" />
          </div>
          <div class="form-group">
            <label>End Year:</label>
            <DatePicker v-model="localBudgetaryEndYearDate" view="year" dateFormat="yy" @date-select="updateBudgetaryEndYear" />
          </div>
        </div>
        <div class="table-container">
          <table class="revenue-table">
            <thead>
              <tr>
                <th class="particulars-col">Particulars</th>
                <th v-for="year in budgetaryYearRange" :key="year">{{ year }}</th>
                <th class="delete-col">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in (keyIssues && keyIssues.budgetary_rows) || []" :key="idx">
                <td class="particulars-col">
                  <Textarea v-model="row.particulars" placeholder="Enter particulars" rows="4" @input="$emit('autoResize', $event)"></Textarea>
                </td>
                <td v-for="year in budgetaryYearRange" :key="year">
                  <InputNumber v-model="row[year]" :min-fraction-digits="2" :max-fraction-digits="2" placeholder="0.00" :class="['revenue-input', { 'negative-value': row[year] < 0 }]" :use-grouping="true" mode="currency" currency="PHP" locale="en-PH" />
                </td>
                <td class="delete-col">
                  <button type="button" @click="$emit('deleteBudgetaryRow', idx)" class="delete-row-btn">Delete</button>
                </td>
              </tr>
              <tr class="total-row">
                <td class="particulars-col total-label">TOTAL</td>
                <td v-for="year in budgetaryYearRange" :key="year" class="total-cell">
                  {{ getYearTotal(keyIssues?.budgetary_rows, year) }}
                </td>
                <td class="delete-col"></td>
              </tr>
            </tbody>
          </table>
        </div>
        <button type="button" @click="$emit('addBudgetaryRow')" class="add-row-btn">Add Row</button>
      </div>
      <div v-show="activeTab === 'dof'" class="key-issues-panel">
        <div class="table-container">
          <table class="revenue-table">
            <thead>
              <tr>
                <th>Concerns</th>
                <th class="delete-col">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in (keyIssues && keyIssues.dof_concerns_rows) || []" :key="idx">
                <td>
                  <textarea v-model="row.concerns" placeholder="Enter DOF concerns" rows="2" @input="$emit('autoResize', $event)"></textarea>
                </td>
                <td class="delete-col">
                  <button type="button" @click="$emit('deleteDofConcernsRow', idx)" class="delete-row-btn">Delete</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <button type="button" @click="$emit('addDofConcernsRow')" class="add-row-btn">Add Row</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import InputNumber from 'primevue/inputnumber'
import Textarea from 'primevue/textarea'
import DatePicker from 'primevue/datepicker'

const props = defineProps({
  keyIssues: {
    type: Object,
    required: true
  },
  activeTab: {
    type: String,
    default: 'revenue'
  },
  revenueStartYear: {
    type: Number,
    default: () => new Date().getFullYear()
  },
  revenueEndYear: {
    type: Number,
    default: () => new Date().getFullYear() + 5
  },
  budgetaryStartYear: {
    type: Number,
    default: () => new Date().getFullYear()
  },
  budgetaryEndYear: {
    type: Number,
    default: () => new Date().getFullYear() + 5
  }
})

const emit = defineEmits([
  'update:activeTab',
  'addRevenueRow',
  'addBudgetaryRow',
  'addDofConcernsRow',
  'deleteRevenueRow',
  'deleteBudgetaryRow',
  'deleteDofConcernsRow',
  'getYearTotal',
  'autoResize',
  'updateRevenueStartYear',
  'updateRevenueEndYear',
  'updateBudgetaryStartYear',
  'updateBudgetaryEndYear'
])

const localRevenueStartYearDate = ref(null)
const localRevenueEndYearDate = ref(null)
const localBudgetaryStartYearDate = ref(null)
const localBudgetaryEndYearDate = ref(null)
const localRevenueStartYear = ref(null)
const localRevenueEndYear = ref(null)
const localBudgetaryStartYear = ref(null)
const localBudgetaryEndYear = ref(null)
const getMaxYearFromData = (rows) => {
  if (!rows || !Array.isArray(rows) || rows.length === 0) return new Date().getFullYear()
  let maxYear = new Date().getFullYear()
  rows.forEach(row => {
    Object.keys(row).forEach(key => {
      const year = parseInt(key)
      if (!isNaN(year) && year > maxYear) {
        maxYear = year
      }
    })
  })
  return maxYear
}

watch(() => props.revenueStartYear, (newVal) => {
  if (!isNaN(newVal)) {
    localRevenueStartYear.value = newVal
    localRevenueStartYearDate.value = new Date(newVal, 0, 1)
  }
}, { immediate: true })

watch(() => props.revenueEndYear, (newVal) => {
  const maxYear = getMaxYearFromData(props.keyIssues?.revenue_rows)
  const year = Math.max(isNaN(newVal) ? new Date().getFullYear() + 5 : newVal, maxYear)
  localRevenueEndYear.value = year
  localRevenueEndYearDate.value = new Date(year, 0, 1)
}, { immediate: true })

watch(() => props.budgetaryStartYear, (newVal) => {
  if (!isNaN(newVal)) {
    localBudgetaryStartYear.value = newVal
    localBudgetaryStartYearDate.value = new Date(newVal, 0, 1)
  }
}, { immediate: true })

watch(() => props.budgetaryEndYear, (newVal) => {
  const maxYear = getMaxYearFromData(props.keyIssues?.budgetary_rows)
  const year = Math.max(isNaN(newVal) ? new Date().getFullYear() + 5 : newVal, maxYear)
  localBudgetaryEndYear.value = year
  localBudgetaryEndYearDate.value = new Date(year, 0, 1)
}, { immediate: true })

watch(() => props.keyIssues, () => {
  const revenueMaxYear = getMaxYearFromData(props.keyIssues?.revenue_rows)
  const budgetaryMaxYear = getMaxYearFromData(props.keyIssues?.budgetary_rows)
  
  const revenueYear = Math.max(isNaN(props.revenueEndYear) ? new Date().getFullYear() + 5 : props.revenueEndYear, revenueMaxYear)
  const budgetaryYear = Math.max(isNaN(props.budgetaryEndYear) ? new Date().getFullYear() + 5 : props.budgetaryEndYear, budgetaryMaxYear)
  
  localRevenueEndYear.value = revenueYear
  localBudgetaryEndYear.value = budgetaryYear
  localRevenueEndYearDate.value = new Date(revenueYear, 0, 1)
  localBudgetaryEndYearDate.value = new Date(budgetaryYear, 0, 1)
}, { deep: true, immediate: true })

const revenueYearRange = computed(() => {
  const startYear = localRevenueStartYear.value || props.revenueStartYear || new Date().getFullYear()
  const maxYear = getMaxYearFromData(props.keyIssues?.revenue_rows)
  const endYear = Math.max(localRevenueEndYear.value || props.revenueEndYear || new Date().getFullYear() + 5, maxYear)
  const years = []
  for (let year = startYear; year <= endYear; year++) {
    years.push(year)
  }
  return years
})

const budgetaryYearRange = computed(() => {
  const startYear = localBudgetaryStartYear.value || props.budgetaryStartYear || new Date().getFullYear()
  const maxYear = getMaxYearFromData(props.keyIssues?.budgetary_rows)
  const endYear = Math.max(localBudgetaryEndYear.value || props.budgetaryEndYear || new Date().getFullYear() + 5, maxYear)
  const years = []
  for (let year = startYear; year <= endYear; year++) {
    years.push(year)
  }
  return years
})
const updateRevenueStartYear = (date) => {
  localRevenueStartYear.value = date.getFullYear()
  emit('updateRevenueStartYear', date.getFullYear())
}

const updateRevenueEndYear = (date) => {
  localRevenueEndYear.value = date.getFullYear()
  emit('updateRevenueEndYear', date.getFullYear())
}

const updateBudgetaryStartYear = (date) => {
  localBudgetaryStartYear.value = date.getFullYear()
  emit('updateBudgetaryStartYear', date.getFullYear())
}

const updateBudgetaryEndYear = (date) => {
  localBudgetaryEndYear.value = date.getFullYear()
  emit('updateBudgetaryEndYear', date.getFullYear())
}

const getYearTotal = (rows, year) => {
  if (!rows || !Array.isArray(rows)) return '₱0.00'
  const total = rows.reduce((sum, row) => {
    const value = parseFloat(row[year]) || 0
    return sum + value
  }, 0)
  return new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(total)
}
</script>

<style scoped>
.key-issues-section {
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid #e5e7eb;
 
  margin-top: 2rem;
}

.key-issues-section h4 {
  color: #92400e;
  font-size: 1.2rem;
  font-weight: 700;
  margin: 0 0 1rem 0;
  padding-bottom: 0.5rem;
  /* border-bottom: 3px solid #f59e0b; */
}

.key-issues-tabs {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.key-issues-btn {
  background: #f3f4f6;
  border: 2px solid #e5e7eb;
  color: #374151;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 0.85rem;
}

.key-issues-btn:hover {
  background: #e5e7eb;
  border-color: #d1d5db;
}

.key-issues-btn.active {
  background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%);
  color: white;
  border-color: #f59e0b;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.key-issues-content {
  /* background: white;
  border-radius: 8px;
  padding: 1rem;
  border: 1px solid #e5e7eb; */
}

.key-issues-panel {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.year-range-controls {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.year-range-controls .form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.year-range-controls label {
  font-weight: 600;
  color: #374151;
  font-size: 0.9rem;
}

.year-range-controls input {
  padding: 0.5rem;
  border: 2px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.9rem;
  width: 100px;
}

.table-container {
  overflow-x: auto;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  margin-bottom: 1rem;
}

.revenue-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
}

.revenue-table th {
  /* background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%); */
  color: black;
  padding: 0.75rem;
  text-align: left;
  font-weight: 600;
  font-size: 0.9rem;
  border-bottom: 2px solid #f59e0b;
}

.revenue-table td {
  padding: 0.5rem;
  border-bottom: 1px solid #e5e7eb;
  vertical-align: top;
}

.particulars-col {
  min-width: 200px;
  width: 30%;
}

.delete-col {
  width: 60px;
  text-align: center;
}

.revenue-table td:not(.particulars-col):not(.delete-col) {
  min-width: 120px;
  text-align: center;
}

.revenue-table textarea {
  width: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  padding: 0.5rem;
  font-size: 0.85rem;
  resize: vertical;
  min-height: 60px;
}

.revenue-table textarea:focus {
  outline: none;
  border-color: #f59e0b;
  box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.1);
}

.revenue-input {
  width: 100%;
  text-align: right;
}

.revenue-input :deep(.p-inputnumber-input) {
  text-align: right;
  padding: 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 0.85rem;
}

.revenue-input :deep(.p-inputnumber-input:focus) {
  outline: none;
  border-color: #f59e0b;
  box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.1);
}

/* Target negative values in InputNumber */
.negative-value :deep(.p-inputnumber-input) {
  color: #dc2626 !important;
}

.negative-value :deep(.p-inputnumber-input:focus) {
  color: #dc2626 !important;
  border-color: #dc2626;
  box-shadow: 0 0 0 2px rgba(220, 38, 38, 0.1);
}

.total-row {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  font-weight: 700;
}

.total-label {
  color: #92400e;
  font-weight: 700;
  text-align: center;
}

.total-cell {
  color: #92400e;
  font-weight: 700;
  text-align: right;
  padding: 0.75rem;
}

.add-row-btn {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.add-row-btn:hover {
  background: linear-gradient(135deg, #047857 0%, #10b981 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);
}

.delete-row-btn {
  background: #dc2626;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 12px;
  font-weight: bold;
  transition: all 0.3s;
  margin: 0 auto;
}

.delete-row-btn:hover {
  background: #b91c1c;
  transform: scale(1.1);
}

@media (max-width: 768px) {
  .key-issues-tabs {
    flex-direction: column;
  }
  
  .year-range-controls {
    flex-direction: column;
  }
  
  .year-range-controls input {
    width: 100%;
  }
  
  .revenue-table {
    font-size: 0.8rem;
  }
  
  .revenue-table th,
  .revenue-table td {
    padding: 0.5rem 0.25rem;
  }
  
  .particulars-col {
    min-width: 150px;
  }
  
  .revenue-table td:not(.particulars-col) {
    min-width: 80px;
  }
}
</style>